import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';

import Modal from 'components/Modal/Modal';
import Help from 'components/Help/Help';
import ProjectInfoPanel from 'components/ProjectInfoPanel/ProjectInfoPanel';
import SubmitButton from 'components/SubmitButton/SubmitButton';
import FormikProjectInfoPanel from 'components/forms/formik/FormikProjectInfoPanel/FormikProjectInfoPanel';
import FormikFieldWatcher from 'components/forms/formik/FormikFieldWatcher/FormikFieldWatcher';
import FormikFieldError from 'components/forms/formik/FormikFieldError/FormikFieldError';
import { useNumberFormatters } from 'hooks/useNumberFormatters';

import { BidSheetRow } from '../../hooks/useBidSheetState';
import ColorLegend from '../ColorLegend/ColorLegend';

import ElementEntry from './components/ElementEntry/ElementEntry';
import useSchema from './hooks/useSchema';
import useFormChangeWatcher from './hooks/useFormChangeWatcher';

import './DetailedBidEntryModal.scss';
import useInitialValues from './hooks/useInitialValues';

interface DetailedBidEntryProps {
  onRequestClose: () => void;
  bid: BidSheetRow;
  saveBid: (bid: BidSheetRow) => void;
}

const DetailedBidEntry = ({ bid, saveBid, onRequestClose }: DetailedBidEntryProps) => {
  const { t } = useTranslation();
  const schema = useSchema(bid);
  const watcher = useFormChangeWatcher();
  const initialValues = useInitialValues(bid);
  const { dollarValueFormatter, numberValueFormatter, percentValueFormatter, dollar4DecimalsValueFormatter } =
    useNumberFormatters();

  return (
    <div className="enkrateia-bid-entry-modal">
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={(values) => {
          saveBid(values);
          onRequestClose();
        }}
      >
        <Form>
          <FormikFieldWatcher onFormChange={watcher} keys={watcher.keys} />
          <div className="top-row">
            <ProjectInfoPanel variant="prosfora" label={t('pages.bidSheet.issuanceYear')} value={bid.issuanceYear} />
            <ProjectInfoPanel variant="prosfora" label={t('pages.bidSheet.issuanceName')} value={bid.issuanceName} />
          </div>
          <div className="form-elements">
            <ElementEntry
              name="volume"
              label={t('pages.bidSheet.volume')}
              hint={t('pages.bidSheet.volumeLabelHint')}
              sliderHint={t('pages.bidSheet.volumeSliderHint')}
              bid={bid}
              valueFormatter={numberValueFormatter}
            />
            <ElementEntry
              name="bidPrice"
              label={t('pages.bidSheet.bidPrice')}
              hint={t('pages.bidSheet.bidPriceLabelHint')}
              sliderHint={t('pages.bidSheet.bidPriceSliderHint')}
              bid={bid}
              prefix="$"
              decimalScale={2}
              valueFormatter={dollarValueFormatter}
            />
            <FormikProjectInfoPanel
              variant="prosfora"
              label={t('pages.bidSheet.subTotal')}
              fieldName="subTotal"
              valueFormatter={dollarValueFormatter}
            />
            <div />
            <div />
            <ElementEntry
              name="downPayment"
              label={t('pages.bidSheet.downPayment')}
              hint={t('pages.bidSheet.downPaymentLabelHint')}
              sliderHint={t('pages.bidSheet.downPaymentSliderHint')}
              bid={bid}
              prefix="$"
              decimalScale={2}
              valueFormatter={dollarValueFormatter}
            />
            <ElementEntry
              name="discount"
              label={t('pages.bidSheet.discount')}
              hint={t('pages.bidSheet.discountLabelHint')}
              sliderHint={t('pages.bidSheet.discountSliderHint')}
              bid={bid}
              suffix="%"
              decimalScale={2}
              valueFormatter={percentValueFormatter}
            />
            <FormikProjectInfoPanel
              variant="prosfora"
              label={t('pages.bidSheet.discountedCredits')}
              fieldName="discountedCredits"
              valueFormatter={numberValueFormatter}
            />
            <FormikProjectInfoPanel
              variant="prosfora"
              label={t('pages.bidSheet.total')}
              fieldName="total"
              valueFormatter={dollarValueFormatter}
            />
            <FormikProjectInfoPanel
              variant="prosfora"
              label={t('pages.bidSheet.avgPrice')}
              fieldName="averagePrice"
              valueFormatter={dollar4DecimalsValueFormatter}
            />
          </div>
          <div className="errors-row">
            <FormikFieldError name="discountedCredits" />
            <FormikFieldError name="total" />
            <FormikFieldError name="averagePrice" />
          </div>
          <div className="submit-row">
            <SubmitButton variant="dark-green-filled">{t('pages.bidSheet.bidEditModal.submitEntry')}</SubmitButton>
          </div>
          <ColorLegend />
        </Form>
      </Formik>
    </div>
  );
};

interface DetailedBidEntryModalProps extends Omit<DetailedBidEntryProps, 'bid'> {
  bid: BidSheetRow | null | undefined;
}

const DetailedBidEntryModal = ({ onRequestClose, bid, ...props }: DetailedBidEntryModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      isOpen={!!bid}
      onRequestClose={onRequestClose}
      variant="kliparo"
      shouldCloseOnOverlayClick={false}
      title={
        <>
          {t('pages.bidSheet.bidEditModal.title')}
          <Help>{t('pages.bidSheet.bidEditModal.titleHelp')}</Help>
        </>
      }
    >
      {bid && <DetailedBidEntry {...props} bid={bid} onRequestClose={onRequestClose} />}
    </Modal>
  );
};

export default DetailedBidEntryModal;
